/* eslint-disable sort-keys-fix/sort-keys-fix */
import { toTypedRxJsonSchema } from 'rxdb';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { DATE_TIME_ISO_SCHEMA } from '../rxdb.constants';

const questionnaireFieldTemplateSchemaLiteral = {
  title: 'questionnaireFieldTemplate',
  description: 'Schema for questionnaire field templates',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    createdAt: DATE_TIME_ISO_SCHEMA,
    updatedAt: DATE_TIME_ISO_SCHEMA,
    allowUnknownValues: { type: 'boolean' },
    aiDeduplicationLogic: { type: ['string', 'null'] },
    autofill: {
      type: ['object', 'null'],
      properties: {
        id: { type: 'string' },
        createdAt: DATE_TIME_ISO_SCHEMA,
        updatedAt: DATE_TIME_ISO_SCHEMA,
        deletedAt: { type: ['string', 'null'] },
        expressionId: { type: ['string', 'null'] },
        action: { type: 'string' },
        description: { type: ['string', 'null'] },
        expression: {
          type: ['object', 'null'],
          properties: {
            id: { type: 'string' },
            createdAt: DATE_TIME_ISO_SCHEMA,
            updatedAt: DATE_TIME_ISO_SCHEMA,
            deletedAt: { type: ['string', 'null'] },
            expression: { type: 'string' },
            filterExpression: { type: ['string', 'null'] },
            filterExpressionVariableName: { type: ['string', 'null'] },
            filterExpressionOperand: { type: ['string', 'null'] },
            filterExpressionItemSelector: { type: ['string', 'null'] },
            filterExpressionReducer: { type: ['string', 'null'] },
            variables: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  createdAt: DATE_TIME_ISO_SCHEMA,
                  updatedAt: DATE_TIME_ISO_SCHEMA,
                  deletedAt: { type: ['string', 'null'] },
                  variableName: { type: 'string' },
                  selector: { type: 'string' },
                  expressionId: { type: 'string' },
                  referenceId: { type: 'string' },
                  referenceType: { type: 'string' },
                },
              },
            },
          },
        },
      },
    },
    conditionExpression: { type: ['object', 'null'] },
    descriptionAST: { type: ['object', 'null'] },
    descriptionHtml: { type: ['string', 'null'] },
    descriptionText: { type: ['string', 'null'] },
    fieldAssigneeTemplates: { items: { type: 'object' }, type: 'array' },
    hasPdfFills: { type: 'boolean' },
    hint: { type: ['string', 'null'] },
    isEditableInTableView: { type: 'boolean' },
    isRequired: { type: 'boolean' },
    isSubtitleField: { type: 'boolean' },
    isTitleField: { type: 'boolean' },
    isViewableInTableView: { type: 'boolean' },
    isVirtual: { type: 'boolean' },
    key: { type: 'string' },
    label: { type: 'string' },
    linkedFieldKeys: {
      items: { type: 'string' },
      type: 'array',
    },
    linkedFields: {
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          createdAt: DATE_TIME_ISO_SCHEMA,
          updatedAt: DATE_TIME_ISO_SCHEMA,
          deletedAt: { type: ['string', 'null'] },
          fieldTemplateId: { type: 'string' },
          linkedFieldTemplateId: { type: 'string' },
          fieldTemplateIndex: { type: ['number', 'null'] },
          linkedFieldTemplateIndex: { type: ['number', 'null'] },
        },
      },
      type: 'array',
    },
    listFieldTemplateId: { type: ['string', 'null'] },
    listFilterExpression: { type: ['object', 'null'] },
    listFilterExpressionId: { type: ['string', 'null'] },
    listItemTemplates: {
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          createdAt: DATE_TIME_ISO_SCHEMA,
          updatedAt: DATE_TIME_ISO_SCHEMA,
          deletedAt: { type: ['string', 'null'] },
          questionnaireTemplateId: { type: 'string' },
          conditionExpressionId: { type: ['string', 'null'] },
          autofillId: { type: ['string', 'null'] },
          sectionTemplateId: { type: 'string' },
          listFieldTemplateId: { type: 'string' },
          rank: { type: 'number' },
          key: { type: 'string' },
          label: { type: 'string' },
          descriptionAST: { type: ['object', 'null'] },
          descriptionHtml: { type: ['string', 'null'] },
          descriptionText: { type: ['string', 'null'] },
          hint: { type: ['string', 'null'] },
          placeholder: { type: ['string', 'null'] },
          type: { type: 'string' },
          isRequired: { type: 'boolean' },
          options: {
            anyOf: [{ type: 'array', items: { type: 'object' } }, { type: 'null' }, { type: 'object', properties: {}, additionalProperties: true }],
          },
          referencedList: {
            type: ['object', 'null'],
            properties: {
              fieldKey: { type: 'string' },
              fieldLabel: { type: 'string' },
              subFieldKey: { type: 'string' },
              subFieldLabel: { type: 'string' },
              isReferencingExistingList: { type: 'boolean' },
            },
          },
          validation: { type: ['object', 'null'] },
          view: { type: ['string', 'null'] },
          subsection: { type: ['string', 'null'] },
          listSubsection: { type: ['string', 'null'] },
          isEditableInTableView: { type: 'boolean' },
          isViewableInTableView: { type: 'boolean' },
          isTitleField: { type: 'boolean' },
          isSubtitleField: { type: 'boolean' },
          aiDeduplicationLogic: { type: ['string', 'null'] },
          listFilterExpressionId: { type: ['string', 'null'] },
          listItemTemplates: { type: 'array', items: { type: 'object' } },
          tableColumns: { type: 'array', items: { type: 'object' } },
          fieldAssigneeTemplates: { type: 'array', items: { type: 'object' } },
          conditionExpression: { type: ['object', 'null'] },
          listFilterExpression: { type: ['object', 'null'] },
          autofill: { type: ['object', 'null'] },
          pdfFills: { type: 'array', items: { type: 'object' } },
        },
      },
      type: 'array',
    },
    linkedListVirtualFieldTemplates: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    listSubsection: { type: ['string', 'null'] },
    numLinkedFields: { type: 'number' },
    options: {
      anyOf: [
        {
          items: { type: 'object' },
          type: 'array',
        },
        { type: 'null' },
        { type: 'object', properties: {}, additionalProperties: true },
      ],
    },
    placeholder: { type: ['string', 'null'] },
    questionnaireTemplateId: { type: 'string', maxLength: 100 },
    rank: { type: 'number' },
    referencedList: {
      properties: {
        fieldKey: { type: 'string' },
        fieldLabel: { type: 'string' },
        isReferencingExistingList: { type: 'boolean' },
        subFieldKey: { type: 'string' },
        subFieldLabel: { type: 'string' },
      },
      type: ['object', 'null'],
    },
    sectionTemplateId: { type: 'string', maxLength: 100 },
    subsection: { type: ['string', 'null'] },
    tableColumns: {
      items: { type: 'object' },
      type: 'array',
    },
    type: { type: 'string' },
    validation: { type: ['object', 'null'] },
    view: { type: ['string', 'null'] },
  },
  required: ['id', 'key', 'label', 'type', 'sectionTemplateId', 'questionnaireTemplateId', 'allowUnknownValues', 'createdAt', 'updatedAt'],
  indexes: ['questionnaireTemplateId'],
} as const;

const schemaTyped = toTypedRxJsonSchema(questionnaireFieldTemplateSchemaLiteral);

export type RxQuestionnaireFieldTemplateDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>;

export const questionnaireFieldTemplateSchema: RxJsonSchema<RxQuestionnaireFieldTemplateDocType> = questionnaireFieldTemplateSchemaLiteral;
